import { apiClient } from "@/lib/api-client"

export type VacancyStatus = "Active" | "Published" | "Closed" | "Expired"

export interface VacancyBrief {
  id: string
  title: string
  organizationName: string | null
  status: VacancyStatus
}

export type ExtractionStatus = "Extracted" | "Failed"
export type ExtractionFailureReason =
  | "None"
  | "InsufficientWalletBalance"
  | "AiServiceError"

export interface PublicCvExtractResponse {
  blobUrl: string
  fileName: string
  contentType: string
  fileSize: number
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  location: string | null
  skills: string[]
  certifications: string[]
  experienceYears: number
  education: string | null
  idNumber: string | null
  extractionStatus?: ExtractionStatus
  extractionFailureReason?: ExtractionFailureReason
}

export interface SubmitApplicationPayload {
  blobUrl: string
  fileName: string
  contentType: string
  fileSize: number
  vacancyId: string
  organizationId: string
  captchaToken: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  location: string | null
  skills: string[]
  certifications: string[]
  experienceYears: number
  education: string | null
  idNumber: string | null
}

export const getVacancyBrief = async (
  organizationId: string,
  vacancyId: string,
): Promise<VacancyBrief> => {
  const response = await apiClient.get(
    `/organizations/${organizationId}/vacancies/${vacancyId}`,
  )
  const v = response.data.value ?? response.data
  return {
    id: v.id,
    title: v.title,
    organizationName: v.organizationName ?? null,
    status: v.status as VacancyStatus,
  }
}

export const extractAndStageCv = async (
  vacancyId: string,
  organizationId: string,
  file: File,
  captchaToken: string,
): Promise<PublicCvExtractResponse> => {
  const formData = new FormData()
  formData.append("CvFile", file)
  formData.append("VacancyId", vacancyId)
  formData.append("OrganizationId", organizationId)
  formData.append("CaptchaToken", captchaToken)

  const response = await apiClient.post(
    `/organizations/${organizationId}/candidates/public/cv/extract`,
    formData,
  )
  return response.data.value
}

export const submitPublicApplication = async (
  payload: SubmitApplicationPayload,
): Promise<void> => {
  await apiClient.post(
    `/organizations/${payload.organizationId}/candidates/public/apply`,
    payload,
  )
}

export interface ApplicantTermsDoc {
  version: string
  content: string
}

export const fetchApplicantTerms = async (): Promise<ApplicantTermsDoc> => {
  const response = await apiClient.get(`/legal-documents/applicant-terms`)
  return response.data as ApplicantTermsDoc
}

export const recordApplicantConsent = async (payload: {
  termsVersion: string
  applicantEmail?: string | null
}): Promise<void> => {
  await apiClient.post(`/legal-documents/applicant-consent`, {
    termsVersion: payload.termsVersion,
    applicantEmail: payload.applicantEmail ?? null,
  })
}
